/**
 * UC3 Relationships classes in plain words (client, 12 Oct 2026, NOTES #760).
 *
 * The backend's `local_bivariate` payload keeps the method's class codes (`positive_linear`,
 * `concave`, ...); everything the reader sees is a frontend string keyed by class and factor:
 *
 * * a plain label (`intel.uc3.lbr.plain.<class>`: "Higher together"), on the map legend, the
 *   result cards, the share bars and the compare view;
 * * a hover sentence naming the factor (`intel.uc3.lbr.hint.<class>` with
 *   `intel.uc3.lbr.subject.<ndvi|rain|soc|ph|sgcec|sgn>`: "Where vegetation is higher,
 *   production is higher."), with the method's own term on a second line;
 * * the method's term (`intel.uc3.lbr.class.<class>`) in the hover and as a small secondary
 *   column of each run's stats table.
 *
 * Legend order puts the five patterns first and "No clear link" (grey) last; the colours are
 * the category raster's own (`LBR_CLASS_COLORS`).
 *
 * Words: co-occurrence only ("higher together", "related"), never one variable acting on
 * the other (`lbrClasses.test.ts` sweeps the labels and sentences).
 */
import type { TFunction } from 'i18next';

import { LBR_CLASS_COLORS, type LbrClassKey, type LbrFactor } from '../../lib/analysis';
import type { ResultLegendEntry } from '../../lib/state';

/** The reader's order: the five patterns, then No clear link. */
export const LBR_LEGEND_ORDER: readonly LbrClassKey[] = [
  'positive_linear',
  'negative_linear',
  'concave',
  'convex',
  'undefined_complex',
  'not_significant',
];

/** The raster's class codes (0..5), the backend's `CLASS_KEYS` order. */
export const LBR_CLASS_CODES: Record<LbrClassKey, number> = {
  not_significant: 0,
  positive_linear: 1,
  negative_linear: 2,
  concave: 3,
  convex: 4,
  undefined_complex: 5,
};

/** Which factor word a sentence names: the soil variable for Soil, `generic` for none. */
export type LbrSubject = 'ndvi' | 'rain' | 'soc' | 'ph' | 'sgcec' | 'sgn' | 'generic';

const SOIL_SUBJECTS = ['soc', 'ph', 'sgcec', 'sgn'] as const;

export function lbrSubject(factor: LbrFactor | null | undefined, soilVar?: string | null): LbrSubject {
  if (factor === 'ndvi' || factor === 'rain') return factor;
  if (factor === 'soil') {
    return (SOIL_SUBJECTS as readonly string[]).includes(soilVar ?? '') ? (soilVar as LbrSubject) : 'soc';
  }
  return 'generic';
}

type T = TFunction | ((key: string, options?: Record<string, unknown>) => string);

/** "Higher together". */
export function lbrLabel(t: T, key: LbrClassKey): string {
  return t(`intel.uc3.lbr.plain.${key}`);
}

/**
 * "Where vegetation is higher, production is higher." With no factor named ("the factor",
 * the reading block), a class whose sentence does not read with an article takes its own
 * wording (`intel.uc3.lbr.hintGeneric.<class>`: "lowest at middle values of the factor").
 */
export function lbrSentence(t: T, key: LbrClassKey, subject: LbrSubject): string {
  const factor = t(`intel.uc3.lbr.subject.${subject}`);
  const plain = t(`intel.uc3.lbr.hint.${key}`, { factor });
  return subject === 'generic' ? t(`intel.uc3.lbr.hintGeneric.${key}`, { defaultValue: plain }) : plain;
}

/** The method's own term: "Positive linear". */
export function lbrMethodTerm(t: T, key: LbrClassKey): string {
  return t(`intel.uc3.lbr.class.${key}`);
}

/** The two-line hover: the sentence, then "Method term: Positive linear". */
export function lbrHint(t: T, key: LbrClassKey, subject: LbrSubject): string {
  return `${lbrSentence(t, key, subject)}\n${t('intel.uc3.lbr.hintMethod', { term: lbrMethodTerm(t, key) })}`;
}

/** The run layer's map legend, in reader order, with plain labels and hovers. */
export function lbrLegend(t: T, subject: LbrSubject): ResultLegendEntry[] {
  return LBR_LEGEND_ORDER.map((key) => ({
    value: LBR_CLASS_CODES[key],
    label: lbrLabel(t, key),
    color: LBR_CLASS_COLORS[key],
    hint: lbrHint(t, key, subject),
  }));
}
